import { datadogRum } from '@datadog/browser-rum'
import useSetCheckoutRuleSession from '../../graphQL/mutations/setCheckoutRuleSession'
import type { ICheckoutRuleValue } from '../../graphQL/queries/getCheckoutRule/types'
import type { CheckoutRuleDocumentType } from '../../interfaces'
import { addAlert } from '../../redux/slices/config'
import {
  clearSelection,
  setGuidedReturn,
  setSelection,
} from '../../redux/slices/checkout'
import { selectFirstHeldRule } from '../../utils/checkoutRuleSelection'
import { generateDataDogContext } from '../../utils/dataDog'
import getAlertErrorMessage, {
  AlertMessageType,
} from '../../utils/getAlertErrorMessage'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import { useDynamicBack } from '../useBack'
import usePushView from '../usePushView'

const FUNCTION_NAME = 'useCheckoutRuleSelection'

/** How a caller other than a picker screen wants `select` to behave. */
export interface CheckoutRuleSelectionOptions {
  /**
   * Suppress the failure toast. The rollback and the Datadog report stay.
   *
   * For COM-1048's auto-settle, which writes a one-option rule on cart entry
   * with no shopper action behind it. A toast reading "we could not save your
   * payment method" over a tap they never made tells them something is wrong
   * and gives them nothing to do about it — and the recovery is automatic,
   * through COM-1046's *Continuar* flow onto the picker.
   *
   * Belt-and-braces rather than mechanism: `select` already raises nothing
   * when the copy is unauthored, because `getAlertErrorMessage` returns `''`
   * and the dispatch is guarded on it. This says so at the call site instead
   * of leaving it to a storefront's text file.
   *
   * ⚠️ It suppresses **only** the toast. The Datadog error still fires, and it
   * has to: a write the shopper never asked for is the one failure they cannot
   * report themselves.
   */
  silent?: boolean
}

/**
 * Choosing, confirming and leaving — for either picker screen.
 *
 * The two screens' write paths differ in three values and nothing else: the
 * rule type, the toast to raise when the write fails, and the name of the
 * field their action's args arrive under. The first two are parameters here;
 * the third stays in each screen's own hook, because that is the part a
 * storefront authors and it reads better named — `setDeliveryDate({ date })`
 * rather than a generic `select`.
 *
 * Extracted rather than copied because the interesting part is the rollback,
 * and a second copy of it is a second place for a rollback bug to live. Same
 * reasoning as `useCheckoutRuleOptions` on the read side.
 */
export const useCheckoutRuleSelection = (
  ruleType: CheckoutRuleDocumentType,
  alertType: AlertMessageType,
  { silent = false }: CheckoutRuleSelectionOptions = {},
) => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const pushView = usePushView()
  const goBack = useDynamicBack()
  const slug = useAppSelector((state) => state.defaultSlice.storeName)
  const sessionId = useAppSelector((state) => state.defaultSlice.sessionId)
  const texts = useAppSelector((state) => state.defaultSlice.config?.texts)
  const [writeSelection] = useSetCheckoutRuleSession()

  /**
   * Records the pick optimistically, then persists it.
   *
   * **The store is read through `useAppStore`, not `useAppSelector`.** The
   * options and the previous selection are read when the shopper taps, and the
   * rollback runs a round trip after that — a render-time snapshot would be as
   * old as the closure and would restore a selection that had already moved.
   * The same reason the boot prefetch hooks read fresh (COM-974).
   *
   * `chosen` is already extracted and coerced by the caller. An empty string
   * means the args carried no usable value, and leaves the shopper where they
   * are.
   *
   * **Answers whether the write landed**, `false` on both the early return and
   * the rejection. The picker screens ignore it — their answer is the toast
   * and the rolled-back row. COM-1048's auto-settle cannot: it latches its
   * status before the `await`, so it needs to be told which terminal value to
   * settle on. Same shape, and the same reason, as COM-974's prefetch hooks.
   */
  const select = async (chosen: string): Promise<boolean> => {
    if (!chosen || !slug || !sessionId) return false

    const checkout = store.getState().checkoutSlice
    const previous = checkout.selections[ruleType]
    /*
     * The offered option carries the `amount` the write has to echo back.
     *
     * Falling back rather than refusing on a miss. The value always comes from
     * what the screen rendered, so a miss means the option list moved under it
     * — and refusing would give the shopper a tap that does nothing. `amount`
     * is `0` on every entry both resolvers build today, so the fallback is the
     * value, not a guess.
     *
     * ⚠️ On the delivery side the fallback is also what keeps R-8 intact: the
     * token is carried through as the caller gave it, never rebuilt.
     */
    const offered = checkout.available[ruleType]?.data.find(
      (option) => option?.data === chosen,
    )
    const value: ICheckoutRuleValue = offered ?? { data: chosen, amount: 0 }

    dispatch(setSelection({ ruleType, value }))

    try {
      await writeSelection({
        variables: {
          storefrontName: slug,
          sessionUid: sessionId,
          ruleType,
          value,
        },
      })

      return true
    } catch (exception) {
      /*
       * Roll back to what was there, which is not always a selection.
       *
       * `setSelection` cannot express "there was no pick", so a failure on the
       * shopper's *first* choice needs `clearSelection` — otherwise the row
       * stays highlighted over a session that carries nothing, and the confirm
       * CTA stays enabled.
       */
      if (previous) {
        dispatch(setSelection({ ruleType, value: previous }))
      } else {
        dispatch(clearSelection({ ruleType }))
      }

      const message = getAlertErrorMessage(alertType, texts)
      if (!silent && message) dispatch(addAlert({ message }))

      const context = generateDataDogContext({
        title: 'setCheckoutRuleSession failed for a checkout rule',
        extraInfo: { function: FUNCTION_NAME, ruleType },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(
        exception instanceof Error
          ? exception
          : new Error('setCheckoutRuleSession failed'),
        context,
      )

      return false
    }
  }

  /**
   * Confirm. Back to the cart — unless the shopper is mid-walk.
   *
   * Three callers reach this one function from three different places.
   * COM-1045's *Cambiar* row and COM-1050's recovery redirect both start on
   * the cart and end there. COM-1046's guided walk starts on the summary and
   * has to end there, visiting every rule the shopper still owes on the way.
   * `checkoutSlice.guidedReturnPath` is how they are told apart, and its
   * absence is what keeps the cart push the default rather than a branch.
   *
   * **The next step is derived, not queued.** `usePlaceOrderFlow` clears its
   * queue on unmount and `EventManager` mounts one per container, so no queue
   * survives a route change. `selectFirstHeldRule` answers again at every hop.
   *
   * `skip` is this screen's own rule type. A shopper who reaches confirm with
   * nothing chosen would otherwise be pushed at the screen they are standing
   * on — a button that visibly does nothing — because the rule they are
   * looking at is still the first one held.
   *
   * **Deliberately not gated on a selection existing**, unchanged and for the
   * unchanged reason: the CTA's `disabled` binds
   * `checkout.ui.<RULE>.confirmDisabled`, so a storefront that authored it
   * cannot reach here with nothing chosen, and one that did not should still
   * let the shopper leave.
   *
   * The store is read fresh rather than from a render closure. `EventManager`
   * registers its handlers in an effect keyed on the redux state, so a value
   * captured at render can be a navigation behind by the time the shopper
   * taps — the same reason `select` above reads fresh, and COM-974's rule.
   * It also matters within one tap: `select` writes its pick optimistically
   * before awaiting, so by now the rule just chosen already reads settled and
   * the walk moves past it.
   *
   * ⚠️ **Later hops replace rather than push.** Only the first hop, from the
   * summary, is a push — so backing out of a picker lands on the summary with
   * the CTA still reading *Continuar*. Replacing the rest keeps history two
   * deep, so Back from anywhere in the walk reaches the summary instead of
   * walking the shopper backwards through pickers they just completed. It
   * also contains an origin-escaping path for free, which is why only the
   * first hop needs `ESCAPES_ORIGIN` — and `selectFirstHeldRule` applies that
   * to every path it returns anyway.
   */
  const confirm = () => {
    const state = store.getState()
    const storefront = state.defaultSlice.storeName
    const session = state.defaultSlice.sessionId
    if (!storefront || !session) return

    const guidedReturn = state.checkoutSlice?.guidedReturnPath
    if (!guidedReturn) {
      pushView({ path: `/${storefront}/${session}/cart` })
      return
    }

    const next = selectFirstHeldRule(state, ruleType)
    if (next) {
      pushView({ path: next.path, state: { replace: true } })
      return
    }

    dispatch(setGuidedReturn(undefined))
    pushView({ path: guidedReturn, state: { replace: true } })
  }

  /**
   * Leaving without choosing.
   *
   * `useDynamicBack` rather than a bare `navigate(-1)`: this is a webview a
   * shopper can be deep-linked into, and popping an empty history strands
   * them. It falls back to the storefront home when there is nothing to pop.
   *
   * Writes nothing. Opening a picker and backing out settles nothing, which is
   * what COM-1046 reads to decide whether its CTA still says *Continuar*.
   */
  const back = () => goBack({})

  return { select, confirm, back }
}

export default useCheckoutRuleSelection
